<template>
  <!--
    Two shells, the same rows. The bookmark button opens a menu this component owns; the records
    headline is itself the trigger and owns the menu around the rows it is handed. Which one gets
    rendered is the caller's to say, because only the caller knows whether the collection's name is
    on screen for a view to take over.
  -->
  <div
    v-if="headline"
    class="table-views__headline"
  >
    <div class="table-views__name">
      <hr-title-menu-btn
        ref="menu"
        :loading="isRestoringView"
        :label="headlineLabel"
        :marker-label="isViewEdited ? $t('tableViews.labelEdited') : null"
        :accessible-name="headlineAccessibleName"
        :menu-title="$t('tableViews.titleSavedViews')"
        data-e2e="button-records-view-title"
        @show="onMenuShown"
      >
        <template #menu>
          <table-views-menu
            v-bind="menuProps"
            v-on="menuListeners"
          />
        </template>
      </hr-title-menu-btn>
    </div>

    <!--
      Beside the name as well as inside the menu. Once a view has been edited, storing the change
      or going back to what was saved is what the reader came to the name for, and both are one
      press further away behind a menu they have no other reason to open.

      Keeping the work is offered wherever there is work to keep. What differs between the two
      states is which act it is, so the wording carries that rather than the control appearing and
      disappearing: "Save" writes into the view that is open, and "Save as new..." makes one, with
      the ellipsis saying a dialog follows. Offering only "Discard" to a reader who has just
      narrowed a collection to something they want again tomorrow proposes throwing it away as the
      one thing they can do with it.

      Grouped, so the pair moves to the second row together. Left to wrap one at a time they would
      part company, and "Discard" alone under a name is a caption rather than an action.
    -->
    <div
      v-if="isViewEdited"
      class="table-views__actions"
    >
      <hr-supporting-btn
        v-if="canSaveToCurrentView"
        :label="$t('tableViews.buttonSave')"
        data-e2e="button-save-view"
        @click="onSaveToCurrentView"
      />
      <hr-supporting-btn
        v-else
        :label="$t('tableViews.buttonSaveAsNew')"
        data-e2e="button-save-view-as-new"
        @click="onManageView()"
      />
      <!-- Worded as the menu words it, because it is the same act reached from two places. -->
      <hr-subtle-btn
        :label="$t('tableViews.buttonDiscard')"
        data-e2e="button-discard-view"
        @click="onDiscardChanges"
      />
    </div>
  </div>

  <div v-else>
    <!-- The trigger is a slot so a caller can open the same menu from something of its own. -->
    <slot
      name="trigger"
      :is-open="isMenuOpen"
      :toggle="onToggleMenu"
    >
      <hr-icon-menu-btn
        icon="bookmarks"
        data-e2e="button-table-views"
        aria-haspopup="menu"
        :aria-label="$t('tableViews.titleSavedViews')"
        :aria-expanded="isMenuOpen ? 'true' : 'false'"
        :legacy-props="LEGACY_TRIGGER_PROPS"
        @click="onToggleMenu"
      />
    </slot>
    <hr-adaptive-menu
      ref="menu"
      v-model="isMenuOpen"
      :title="$t('tableViews.titleSavedViews')"
    >
      <table-views-menu
        v-bind="menuProps"
        v-on="menuListeners"
      />
    </hr-adaptive-menu>
  </div>
</template>
<script>
import { mapState } from 'pinia';
import SaveTableViewDialog from 'src/components/UI/modals/SaveTableViewDialog';
import { stateSnapshot } from 'src/components/UI/recordsViewState';
import TableViewsMenu from 'src/components/UI/TableViewsMenu.vue';
import { TABLE_VIEW_SHARED_TYPES } from 'src/constants/tableView';
import {
  confirmDialog,
  HrAdaptiveMenu,
  HrIconMenuBtn,
  HrSubtleBtn,
  HrSupportingBtn,
  HrTitleMenuBtn,
  openComponentDialog,
} from 'src/design-system';
import TableViewsDB from 'src/fb/tableViews-db';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { logEvent } from 'src/utils/LogEvent';

// @todo Remove legacy-props when Legacy theme is removed
const LEGACY_TRIGGER_PROPS = { color: 'primary' };

const byTitle = (a, b) => a.tableViewTitle.localeCompare(b.tableViewTitle);

export default {
  name: 'TableViews',

  components: {
    HrAdaptiveMenu,
    HrIconMenuBtn,
    HrSubtleBtn,
    HrSupportingBtn,
    HrTitleMenuBtn,
    TableViewsMenu,
  },

  mixins: [FiltersMixin, Interactions],

  props: {
    context: {
      type: String,
      required: true,
    },

    /**
     * Render the views as the collection's headline rather than as a bookmark button beside it.
     * The loaded view's name becomes the title, and the title becomes the way to change it.
     */
    headline: {
      type: Boolean,
      default: false,
    },

    /**
     * The collection's own name, shown as the headline until a view is loaded. Only read in
     * headline mode, where something has to stand in for a view that has not been chosen yet.
     */
    title: {
      type: String,
      default: null,
    },

    columns: {
      type: Array,
      default: () => ([]),
    },

    filters: {
      type: Object,
      default: () => ({}),
    },

    pagination: {
      type: Object,
      default: () => ({}),
    },

    otherSettings: {
      type: Object,
      default: () => ({}),
    },

    saveMessage: {
      type: String,
      default: '',
    },

    /*
     * The columns themselves, not the filter criteria built from them. jsonParseFilters matches a
     * stored filter to its callback on `name`, and a criterion object carries `value` instead, so
     * criteria here reattach nothing and the match fails silently.
     */
    filterCallbacks: {
      type: Array,
      default: () => [],
    },

    activeViewId: {
      type: String,
      default: null,
    },

    shareableRoles: {
      type: Array,
      default: () => [],
    },

    /**
     * What the collection looks like with no view loaded — its default columns and filters. It is
     * the baseline the working state is measured against while no view is open, which is what lets
     * a reader who has filtered the plain list see that they have something worth saving.
     *
     * Null leaves the no-view state with no baseline at all, and so never edited.
     */
    defaultState: {
      type: Object,
      default: null,
    },

    /**
     * What to call the row that leaves every view behind and shows the collection whole — "All
     * clients". A phrase rather than a name, which is why it arrives composed instead of being
     * built here out of the collection's title: joining "All" to a noun is a sentence, and the noun
     * is not lower case in every language it will be read in.
     */
    defaultViewLabel: {
      type: String,
      default: null,
    },

    /**
     * The view and unsaved edits this reader left behind, read from storage by the caller. The view
     * is looked up by id and loaded as it stands today, then the edits go on top of it. A view that
     * no longer resolves reports `view-unavailable` instead.
     *
     * Applied on mount, and again if it arrives after one — it is keyed by the reader, who is not
     * always known by the time this is built. A late one lands on what the reader has done since,
     * and is dropped rather than applied over it.
     */
    restoreState: {
      type: Object,
      default: null,
    },
  },

  emits: [
    'load-view', 'restore-settled', 'update:activeViewId', 'update:viewState', 'view-saved',
    'view-unavailable',
  ],

  data() {
    return {
      isMenuOpen: false,
      stateWhenNothingRestored: null,
      isLoadingTableViews: false,
      loadedTableViewsContext: null,
      userTableViews: [],
      clinicTableViewsBind: [],
      currentTableView: this.activeViewId,
      loadedViewSnapshot: null,
      // Until the stored state has been put back, what is on screen is not worth storing over it.
      hasRestored: false,
      /*
       * Known from the caller's stored record before the first render, so the headline holds its
       * place rather than naming the collection and being replaced by the view a moment later.
       * Only an id is worth waiting for: stored edits alone are put back with nothing to read.
       */
      isRestoringView: (this.restoreState?.viewId ?? null) !== null,
      LEGACY_TRIGGER_PROPS,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'isManagerOrAdministrator']),

    tableViews() {
      return [...this.userTableViews, ...this.clinicTableViews];
    },

    roleSpecificTableViews() {
      return this.clinicTableViewsBind.filter(
        (view) => view?.visibleToRoles
          && (view?.createdBy === this.userDetails.userId
            || view.visibleToRoles?.includes(this.userDetails.userPrivilege || this.userDetails.role)),
      );
    },

    organizationTableViews() {
      return this.clinicTableViewsBind.filter((view) => !view?.visibleToRoles);
    },

    clinicTableViews() {
      return [...this.organizationTableViews, ...this.roleSpecificTableViews].sort(byTitle);
    },

    /*
     * Resolved here rather than handed out with `load-view`, whose payload is the view's contents
     * rather than the view. Null until the menu has been opened once, because that is when the
     * views load — a trigger showing the name needs its own fallback for that window.
     */
    currentTableViewDoc() {
      return this.tableViews.find((view) => view.id === this.currentTableView) ?? null;
    },

    activeTableViewTitle() {
      return this.currentTableViewDoc?.tableViewTitle ?? null;
    },

    // The four things a view stores, as the working state currently stands.
    currentWorkingState() {
      return {
        columns: this.columns,
        filters: this.filters,
        otherSettings: this.otherSettings,
        rowsPerPage: this.pagination?.rowsPerPage ?? null,
      };
    },

    currentViewSnapshot() {
      return stateSnapshot(this.currentWorkingState);
    },

    /*
     * What "unedited" means right now: the view that is open, or the collection itself when none is.
     *
     * A loaded view is measured against what was installed when it loaded rather than against the
     * document it came from. jsonParseFilters recomputes a relative date filter's from/to against
     * today every time it parses, so a view holding one would never match its own stored JSON and
     * would read as edited from the moment it opened.
     */
    baselineSnapshot() {
      if (this.loadedViewSnapshot !== null) {
        return this.loadedViewSnapshot;
      }
      return this.defaultState === null ? null : stateSnapshot(this.defaultState);
    },

    isViewEdited() {
      return this.baselineSnapshot !== null && this.currentViewSnapshot !== this.baselineSnapshot;
    },

    /*
     * Saving into a shared view is gated the same way its edit pencil is: a clinic view is only
     * writable by a manager or administrator, whoever happens to have it open. With no view open
     * there is nothing to write into, whoever is asking.
     */
    canSaveToCurrentView() {
      if (!this.isViewEdited || this.currentTableView === null) {
        return false;
      }
      return this.userTableViews.some((view) => view.id === this.currentTableView)
        || this.isManagerOrAdministrator;
    },

    /*
     * Everything not in this reader's own list is somebody else's to read as well — a clinic view,
     * whether it reaches the whole organization or only certain roles. The distinction is worth
     * drawing because the same press means two different things: putting your own screen back how
     * you like it, or changing what everyone it reaches opens tomorrow.
     */
    isCurrentViewShared() {
      return this.currentTableViewDoc !== null
        && !this.userTableViews.some((view) => view.id === this.currentTableView);
    },

    /*
     * Absent when the collection declares no defaults: there would be nothing to go back to. A page
     * that has not named the row gets a generic phrase rather than its own title, which would read
     * as the collection's name where the row means the whole of it.
     */
    defaultRowLabel() {
      if (this.defaultState === null) {
        return null;
      }
      return this.defaultViewLabel ?? this.$t('tableViews.defaultViewLabel');
    },

    // What the caller stores: the view being read, and only what the reader changed about it.
    viewStateRecord() {
      return {
        viewId: this.currentTableView,
        edits: this.isViewEdited ? this.currentWorkingState : null,
      };
    },

    /*
     * The headline names whichever row the menu has marked, so the two cannot disagree about what
     * is being read. With no view that row is the collection itself — "All clients", not "Clients",
     * which names the collection rather than the whole of it.
     *
     * A view that is selected but not yet resolved falls back to the collection's name rather than
     * to that row: the views load when the menu first opens, and claiming to show all of them
     * during that window would be claiming the opposite of what is on screen.
     */
    headlineLabel() {
      if (this.currentTableView !== null) {
        return this.activeTableViewTitle ?? this.title;
      }
      return this.defaultRowLabel ?? this.title;
    },

    /*
     * Composed here rather than in the trigger. Joining a name to its marker is a sentence, and the
     * design system has no language to build one in.
     */
    headlineAccessibleName() {
      if (!this.isViewEdited) {
        return this.headlineLabel;
      }
      return this.$t('tableViews.labelViewEdited', { tableViewTitle: this.headlineLabel });
    },

    // Both shells show the same rows, so what the rows need is described once.
    menuProps() {
      return {
        loading: this.isLoadingTableViews,
        userViews: this.userTableViews,
        clinicViews: this.clinicTableViews,
        currentViewId: this.currentTableView,
        canEditClinicViews: this.isManagerOrAdministrator,
        canSaveToCurrentView: this.canSaveToCurrentView,
        isEdited: this.isViewEdited,
        defaultViewLabel: this.defaultRowLabel,
      };
    },

    /*
     * Every one of these closes the menu first. Two of the items sit behind a v-if their own click
     * falsifies — discarding stops the state being edited, saving into the open view does the same
     * — so the item is on its way out of the DOM by the time it would have closed the menu itself,
     * and the panel is left standing open over a list it no longer belongs to. Closing here rather
     * than per item, because any future item that changes what the menu offers lands in the same
     * trap without knowing it.
     */
    menuListeners() {
      const withMenuClosed = (handler) => (...args) => {
        this.closeMenu();
        return handler(...args);
      };

      return {
        'load-view': withMenuClosed(this.onLoadView),
        'edit-view': withMenuClosed((tableView) => this.onManageView(tableView, true)),
        'save-new-view': withMenuClosed(() => this.onManageView()),
        'save-to-current-view': withMenuClosed(this.onSaveToCurrentView),
        'discard-changes': withMenuClosed(this.onDiscardChanges),
        'load-default': withMenuClosed(this.onLoadDefault),
      };
    },
  },

  watch: {
    isLoadingTableViews(isLoading) {
      if (isLoading) {
        return;
      }

      this.$nextTick(() => this.$refs.menu?.updatePosition());
    },

    activeViewId(newVal) {
      if (this.currentTableView !== newVal) {
        this.currentTableView = newVal;
      }
    },

    // No view is no view baseline: the collection's defaults take over as the thing to differ from.
    currentTableView(newVal) {
      if (newVal === null) {
        this.loadedViewSnapshot = null;
      }
    },

    /*
     * Reported rather than stored here: this component knows what is open and what has changed
     * about it, and nothing about where a page keeps things. Held back until the stored state has
     * been put back, or the first render would overwrite it with the defaults it started on.
     */
    viewStateRecord: {
      handler(record) {
        if (this.hasRestored) {
          this.$emit('update:viewState', record);
        }
      },
      deep: true,
    },

    /*
     * The record is read before the first render in the ordinary case, but it is keyed by the
     * reader, and the reader can arrive after this selector has already mounted and restored
     * nothing. Running again is what keeps a late record from being a lost view: settling is what
     * starts the reader's state being stored, so without this the defaults are written over the
     * very record that just turned up.
     *
     * Only on the way out of nothing. What is restored from here on is the reader's own doing.
     */
    restoreState(stored, previous) {
      if (stored === null || previous !== null) {
        return;
      }
      /*
       * Only onto what this selector settled on. Anything the reader did between settling and the
       * record turning up they chose over the very thing being put back, and a view that lands on
       * top of it takes away a choice they made and watched take effect.
       *
       * The open view is compared as well as the working state, because the snapshot does not carry
       * it: a reader who picked a view whose columns and filters match what they were already
       * looking at has changed what is open without changing any of that.
       *
       * Null means the record arrived before this ran at all, and the mount is about to restore it.
       */
      const settled = this.stateWhenNothingRestored;
      if (settled !== null
        && this.currentViewSnapshot === settled.snapshot
        && this.currentTableView === settled.viewId) {
        this.restoreStoredState();
      }
    },
  },

  mounted() {
    this.restoreStoredState();
  },

  methods: {

    /*
     * HrAdaptiveMenu is fully controlled and turns off the desktop menu's parent-click auto-toggle,
     * so the trigger owns open and close on both surfaces.
     */
    // Both panels: the headline owns its own open state, the compact trigger is controlled here.
    closeMenu() {
      this.isMenuOpen = false;

      if (this.headline) {
        this.$refs.menu?.hide();
      }
    }, // closeMenu()

    onToggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;

      if (this.isMenuOpen) {
        this.onMenuShown();
      }
    }, // onToggleMenu()

    /*
     * Both shells arrive here, which is the point of it being a method: the headline announces
     * itself through the trigger's own show event and the bookmark button through onToggleMenu, so
     * a retry wired to one of them covers whichever half of the app happens to use the other.
     *
     * A restore the first read left undecided is tried again rather than abandoned for the session:
     * until it settles nothing the reader changes is reported upward either, so a moment of bad
     * network would otherwise cost them the rest of their visit.
     */
    onMenuShown() {
      if (this.hasRestored) {
        this.onLoadViews();
      } else {
        this.restoreStoredState();
      }
    }, // onMenuShown()

    /**
     * @returns {Promise<boolean>} Whether the views for this context are loaded. False means the
     * read failed, which is not the same as there being none: a caller that acts on an empty list
     * would be acting on a network error.
     */
    async onLoadViews() {
      if (this.loadedTableViewsContext === this.context || this.isLoadingTableViews) {
        return this.loadedTableViewsContext === this.context;
      }
      this.isLoadingTableViews = true;

      const tableViewsDb = new TableViewsDB();
      try {
        const userTableViewActions = tableViewsDb.readAll(
          [
            ['clinic', '==', this.userDetails.clinic],
            ['user', '==', this.userDetails.userId],
            ['context', '==', this.context],
          ],
        );
        const clinicTableViewActions = tableViewsDb.bindAll(
          this,
          'clinicTableViewsBind',
          [
            ['clinic', '==', this.userDetails.clinic],
            ['user', '==', null],
            ['context', '==', this.context],
          ],
        );

        const [userTableViews] = await Promise.all([userTableViewActions, clinicTableViewActions]);
        this.userTableViews = [...userTableViews].sort(byTitle);
        /*
         * Marked loaded only once it is. In the finally block this ran after a failure too, which
         * left an empty list looking exactly like a clinic with no saved views, and never retried.
         */
        this.loadedTableViewsContext = this.context;
        return true;
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err);
        return false;
      } finally {
        this.isLoadingTableViews = false;
      }
    }, // onLoadViews()

    onManageView(currentView = null, isEditing = false) {
      openComponentDialog(
        SaveTableViewDialog,
        {
          allTableViews: this.tableViews,
          canSaveGlobal: this.isManagerOrAdministrator,
          shareableRoles: this.shareableRoles,
          saveMessage: this.saveMessage,
          currentView,
          isEditing,
          onTableViewDeleted: (tableView) => this.handleTableViewDelete(tableView),
        },
        async (tableViewData) => {
          const tableViewsDb = new TableViewsDB();
          const tableView = {
            clinic: this.userDetails.clinic,
            visibleToRoles: tableViewData?.visibleToRoles || null,
            user: tableViewData.shareType === TABLE_VIEW_SHARED_TYPES.PRIVATE ? this.userDetails.userId : null,
            createdBy: tableViewData.createdBy,
            context: this.context,
            tableViewTitle: tableViewData.title,
            columns: JSON.stringify(this.columns),
            filters: JSON.stringify(this.filters),
            pagination: JSON.stringify(this.pagination),
            otherSettings: JSON.stringify(this.otherSettings),
          };
          try {
            this.$q.loading.show({ delay: 10 });
            if (tableViewData.id !== null) {
              await tableViewsDb.set(tableView, tableViewData.id);
              this.currentTableView = tableViewData.id;
            } else {
              const newTableView = await tableViewsDb.create(tableView);
              if (newTableView?.id) {
                this.currentTableView = newTableView.id;
              }
            }
            this.$emit('update:activeViewId', this.currentTableView);
            this.$emit('view-saved', {
              id: this.currentTableView,
              isNew: tableViewData.id === null,
            });
            // What was just written is the new baseline, so the view stops reading as edited.
            this.loadedViewSnapshot = this.currentViewSnapshot;
            this.loadedTableViewsContext = null;
            await this.onLoadViews();
            this.notifySuccess(this.$t('tableViews.notifySavedSuccess', { tableViewTitle: tableViewData.title }));
          } catch (err) {
            this.notifyNegative(err.message);
            logEvent(err);
          } finally {
            this.$q.loading.hide();
          }
        },
      );
    }, // onManageView()

    /*
     * Immediate on a view only this reader opens, and asked about first on a shared one. Saving is
     * not an act anyone expects to be stopped for, which is exactly why the shared case needs the
     * question: it is the only point at which one press rewrites what other people will open, and
     * nothing on the way to it says so.
     */
    onSaveToCurrentView() {
      const tableView = this.currentTableViewDoc;
      if (!tableView) {
        return;
      }
      if (!this.isCurrentViewShared) {
        this.writeToCurrentView(tableView);
        return;
      }

      confirmDialog(
        this.$t('tableViews.confirmOverwriteSharedTitle'),
        this.$t('tableViews.confirmOverwriteSharedMessage', { title: tableView.tableViewTitle }),
        () => this.writeToCurrentView(tableView),
        { confirmLabel: this.$t('tableViews.confirmOverwriteSharedButton') },
      );
    }, // onSaveToCurrentView()

    /*
     * A partial update rather than the whole-document write onManageView does: storing the current
     * state into a view must not touch its title, its owner, or who it is shared with.
     */
    async writeToCurrentView(tableView) {
      try {
        this.$q.loading.show({ delay: 10 });
        await new TableViewsDB().update({
          id: tableView.id,
          columns: JSON.stringify(this.columns),
          filters: JSON.stringify(this.filters),
          pagination: JSON.stringify(this.pagination),
          otherSettings: JSON.stringify(this.otherSettings),
        });
        this.loadedViewSnapshot = this.currentViewSnapshot;
        this.$emit('view-saved', { id: tableView.id, isNew: false });
        this.loadedTableViewsContext = null;
        await this.onLoadViews();
        this.notifySuccess(
          this.$t('tableViews.notifySavedSuccess', { tableViewTitle: tableView.tableViewTitle }),
        );
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err);
      } finally {
        this.$q.loading.hide();
      }
    }, // writeToCurrentView()

    /*
     * Discarding is loading the stored view again, rather than a second path that reinstates state
     * and would have to stay in step with onLoadView as views gain anything else worth restoring.
     */
    onDiscardChanges() {
      if (this.currentTableViewDoc) {
        this.onLoadView(this.currentTableViewDoc);
        return;
      }
      // Nothing loaded means the reader filtered the plain collection, so back is its defaults.
      if (this.defaultState !== null) {
        this.$emit('load-view', this.asLoadPayload(this.defaultState));
      }
    }, // onDiscardChanges()

    /*
     * Out of every view and back to the collection whole. Distinct from discarding, which returns a
     * view to what it stores: this leaves the view behind, so the baseline becomes the collection's
     * own defaults and the stored record goes back to pointing at nothing.
     */
    onLoadDefault() {
      if (this.defaultState === null) {
        return;
      }
      this.currentTableView = null;
      this.loadedViewSnapshot = null;
      this.$emit('update:activeViewId', null);
      this.$emit('load-view', this.asLoadPayload(this.defaultState));
    }, // onLoadDefault()

    /*
     * The baseline is always the view as it stands today, even when edits are put back on top of
     * it. That is what makes a restored edit that the view has since caught up with read as clean:
     * the comparison is against the definition, never against what was stored beside it.
     */
    onLoadView(tableView, edits = null) {
      const newPagination = JSON.parse(tableView.pagination);
      this.currentTableView = tableView.id;
      this.$emit('update:activeViewId', this.currentTableView);

      const loaded = {
        columns: JSON.parse(tableView.columns),
        pagination: {
          ...this.pagination,
          rowsPerPage: newPagination.rowsPerPage ?? null,
          page: 1,
        },
        filters: this.jsonParseFilters(tableView.filters, this.filterCallbacks),
        otherSettings: JSON.parse(tableView.otherSettings ?? '{}'),
      };

      // Taken from the payload rather than the props: the parent has not applied it yet.
      this.loadedViewSnapshot = stateSnapshot({
        columns: loaded.columns,
        filters: loaded.filters,
        otherSettings: loaded.otherSettings,
        rowsPerPage: loaded.pagination.rowsPerPage,
      });
      this.$emit('load-view', edits === null ? loaded : this.asLoadPayload(edits));
    }, // onLoadView()

    /*
     * A stored working state in the shape `load-view` carries, so both paths install state alike.
     *
     * Filters go back through the same parse a view's do, because a stored edit has been through
     * JSON and comes back short of what a filter needs: a date's Timestamps arrive as plain
     * seconds and nanoseconds, a relative period still holds the dates it meant when it was
     * stored, and a filter that matches through a callback has lost the function it matches with.
     * Re-stringified rather than kept as JSON throughout, since the defaults arrive here as live
     * objects and the two have to leave alike.
     */
    asLoadPayload(state) {
      return {
        columns: state.columns,
        pagination: { ...this.pagination, rowsPerPage: state.rowsPerPage ?? null, page: 1 },
        filters: this.jsonParseFilters(JSON.stringify(state.filters ?? {}), this.filterCallbacks),
        otherSettings: state.otherSettings,
      };
    }, // asLoadPayload()

    /*
     * Put back what the reader left: the view as it stands now, then their unsaved changes over it.
     * A view that has since been deleted, or that this reader may no longer see, is reported rather
     * than guessed at — its edits go with it, since they describe a thing that is gone.
     */
    async restoreStoredState() {
      /*
       * One restore at a time. A second one started while the first is still reading would find
       * onLoadViews reporting false — a read is in flight, not a read that failed — and report the
       * attempt over on the strength of it, letting the page fill the place the first is still
       * holding. The first one finishes and reports again, so what this prevents is the flicker in
       * between.
       */
      if (this.isLoadingTableViews) {
        return;
      }
      const stored = this.restoreState;
      if (!stored?.viewId) {
        if (stored?.edits) {
          this.$emit('load-view', this.asLoadPayload(stored.edits));
        }
        /*
         * Nothing at all to put back, which may mean the reader is not known yet rather than that
         * they left nothing. What the selector settles on is kept, so a record arriving later can
         * tell whether it would be landing on that or on something the reader has done since.
         */
        if (stored === null) {
          this.stateWhenNothingRestored = {
            snapshot: this.currentViewSnapshot,
            viewId: this.currentTableView,
          };
        }
        this.settleRestore();
        return;
      }

      const workingStateBeforeRead = this.currentViewSnapshot;

      /*
       * A read that failed says nothing about whether the view still exists, so nothing is decided
       * on it. `hasRestored` deliberately stays false: it is what gates reporting state upward, and
       * reporting the defaults this page is sitting on would overwrite the stored record with them
       * — losing what a reader saved because the network faltered for a moment.
       *
       * The attempt is still over, and the page waiting on it is told so. Deciding nothing is not
       * a reason to leave a reader looking at a placeholder until they reload.
       */
      if (!await this.onLoadViews()) {
        this.isRestoringView = false;
        this.$emit('restore-settled');
        return;
      }

      const tableView = this.tableViews.find((view) => view.id === stored.viewId);

      /*
       * A retry runs with the page already let go of and the reader working in it, since that is
       * what the failed read released them to do. Whatever they changed while this read was in
       * flight they chose over the very thing being put back, so the view lands only where it
       * would land on the state it was stored against. Settling either way is what starts their
       * choice being stored, so the one that won is the one that is kept.
       *
       * The first attempt is not measured that way. Its place is still being held, so nothing on
       * screen is the reader's yet, and a page that moves its own props while the read is in
       * flight would otherwise have the view it stored quietly dropped.
       */
      if (!tableView) {
        this.$emit('view-unavailable');
      } else if (this.isRestoringView || this.currentViewSnapshot === workingStateBeforeRead) {
        this.onLoadView(tableView, stored.edits ?? null);
      }
      this.settleRestore();
    }, // restoreStoredState()

    /*
     * Two things, deliberately not one. Decided is what gates writing the reader's state back, and
     * a failed read must not set it or the defaults overwrite what they stored. Over is what the
     * page waits on to stop holding a place, and that is true however the attempt turned out.
     */
    settleRestore() {
      this.hasRestored = true;
      this.isRestoringView = false;
      this.$emit('restore-settled');
    }, // settleRestore()

    async handleTableViewDelete(tableView) {
      const tableViewsDb = new TableViewsDB();
      try {
        this.$q.loading.show({ delay: 10 });
        await tableViewsDb.delete(tableView.id);
        /*
         * Only the user's own views are held as plain state. The clinic views are a live bind, so
         * removing the document is what updates them.
         */
        if (tableView.tableViewSection === 'userTableViews') {
          this.userTableViews = this.userTableViews.filter(({ id }) => id !== tableView.id);
        }
        this.notifySuccess(
          this.$t('tableViews.notifyDeletedSuccess', { tableViewTitle: tableView.tableViewTitle }),
        );
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err);
      } finally {
        this.$q.loading.hide();
      }
    }, // handleTableViewDelete()
  },

};

</script>

<style lang="scss" scoped>

.table-views__headline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

/*
 * The name holds its full width while it shares a line, which is what pushes the actions onto a row
 * of their own rather than letting the name be cut to keep them company. Only once it is alone on a
 * line does the cap apply, and only then does the label inside it start to truncate — last, and by
 * design.
 */
.table-views__name {
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
}

.table-views__actions {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
}

</style>
